import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle2, XCircle, Clock, FileText } from 'lucide-react';

interface ImportLog {
  id: string;
  filename: string;
  total_rows: number;
  processed_rows: number;
  created_count: number;
  updated_count: number;
  failed_count: number;
  status: string;
  created_at: string;
  completed_at: string | null;
  error_log: any;
}

interface BacklinkActivityLogProps {
  userId: string;
}

export const BacklinkActivityLog = ({ userId }: BacklinkActivityLogProps) => {
  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
    
    // Subscribe to real-time updates
    const channel = supabase
      .channel('import-logs-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'backlink_import_logs',
          filter: `agent_id=eq.${userId}`
        },
        () => {
          fetchLogs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const fetchLogs = async () => {
    const { data, error } = await supabase
      .from('backlink_import_logs')
      .select('*')
      .eq('agent_id', userId)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching logs:', error);
    } else {
      setLogs(data || []);
    }
    setLoading(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-yellow-600 animate-pulse" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-600">Completed</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'processing':
        return <Badge className="bg-yellow-600">Processing</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
          <CardDescription>Loading import history...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity Log</CardTitle>
        <CardDescription>
          Recent backlink import operations - all updates happen automatically
        </CardDescription>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">
            No import history yet. Upload a CSV to get started.
          </p>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-4">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="border rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log.status)}
                      <span className="font-medium">{log.filename}</span>
                    </div>
                    {getStatusBadge(log.status)}
                  </div>

                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Total:</span>{' '}
                      <span className="font-medium">{log.total_rows}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Created:</span>{' '}
                      <span className="font-medium text-green-600">{log.created_count}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Updated:</span>{' '}
                      <span className="font-medium text-blue-600">{log.updated_count}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Failed:</span>{' '}
                      <span className="font-medium text-red-600">{log.failed_count}</span>
                    </div>
                  </div>

                  {log.error_log && log.error_log.length > 0 && (
                    <div className="text-xs text-muted-foreground bg-destructive/10 p-2 rounded">
                      <p className="font-medium mb-1">Errors:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {log.error_log.slice(0, 3).map((err: any, idx: number) => (
                          <li key={idx}>
                            {err.row}: {err.error}
                          </li>
                        ))}
                        {log.error_log.length > 3 && (
                          <li>...and {log.error_log.length - 3} more errors</li>
                        )}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Started: {new Date(log.created_at).toLocaleString()}</span>
                    {log.completed_at && (
                      <span>Completed: {new Date(log.completed_at).toLocaleString()}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};